import React, {Fragment, useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import jwt_decode from 'jwt-decode';
import savingsIcon from '../../images/savings.svg';

import {
  ArrowDown,
  ArrowUp,
  BlueButton,
  Cart,
  CartText,
  CartWrapper,
  Container,
  Description,
  DropDown,
  DropDownWrapper,
  InfoText,
  Left,
  LoadingWrapper,
  Menu,
  MixSaveButton,
  MixSaveQty,
  OldSite,
  Option,
  OptionNoLink,
  OptionRouterLink,
  Quantity,
  Right,
  SavingsBubble,
  Wrapper,
} from './TopBar.style';
import Loading from '../Loading/Loading';
import SystemStatus from '../ServerStatus/SystemStatus';
import {getTopBarWelcomeText} from '../../helpers/string.helper';
import {
  hasMixAndSaveOffer,
  getMixAndSaveTierMessage,
  getMixAndSaveQuantity,
} from '../../helpers/basket.helper';
import {showPriceCompareLink} from '../../helpers/invoiceUpload.helper';
import {PRICE_COMPARISON_PATH} from '../../constants/routePaths';
import {
  AX_BASKET_STATUS,
  BUBBLE_POPUP_TIMEOUT,
  JJ_DOMAIN,
  PRICE_COMPARE_LABEL,
} from '../../constants/constants';

const TopBar = props => {
  const {
    toggleLoginModal,
    quantity,
    toggleMenu,
    navigateToCheckout,
    branch,
    branchList,
    navigateToBookSlot,
    updatingBasketStatus,
    auth,
    profile,
    isMultiAccounts,
    isStaffAccount,
    systemMessages,
    isMaintenanceMode,
    showShoppingList,
    userCookie,
    loginToken,
    basket,
    navigateToRegister,
    toggleSwitchAccountModal,
    openInJJsModal: openInJJsModalProp,
    logout: logoutProp,
    settings,
  } = props;

  const [showDropDown, setShowDropDown] = useState(false);
  const [staffLoginEmail, setStaffLoginEmail] = useState('');
  const [showSavingsBubble, setShowSavingsBubble] = useState(false);
  const [savingsMessage, setSavingsMessage] = useState('');

  const dropdownRef = useRef(null);
  const savingsBubbleTimer = useRef(null);
  const prevBasketRef = useRef(null);

  // Handle login token changes
  useEffect(() => {
    if (loginToken) {
      const loginObj = jwt_decode(loginToken);
      if (loginObj && loginObj.email) {
        setStaffLoginEmail(loginObj.email);
      }
    } else if (staffLoginEmail) {
      setStaffLoginEmail('');
    }
  }, [loginToken, isStaffAccount]);

  // Handle basket changes for savings popup
  useEffect(() => {
    const prevBasket = prevBasketRef.current;

    // only show bubble when the basket is synced
    if (
      basket &&
      basket.synced &&
      basket.syncStatus === AX_BASKET_STATUS.SYNCED
    ) {
      // On initial load (no prevBasket), check if basket has Mix & Save offers
      if (!prevBasket) {
        if (hasMixAndSaveOffer(basket)) {
          showSavingsPopup();
        }
      }
      // On subsequent updates, check if basket savings or quantities changed
      else {
        const currentData = {
          savings: basket.savings,
          potentialSavings: basket.potentialSavings,
          totalQuantity: basket.items?.reduce(
            (sum, item) => sum + (item.quantity || 0),
            0
          ),
        };
        const prevData = {
          savings: prevBasket.savings,
          potentialSavings: prevBasket.potentialSavings,
          totalQuantity: prevBasket.items?.reduce(
            (sum, item) => sum + (item.quantity || 0),
            0
          ),
        };

        const hasChanged =
          JSON.stringify(currentData) !== JSON.stringify(prevData);

        if (hasChanged && hasMixAndSaveOffer(basket)) {
          showSavingsPopup();
        }
      }
    }

    prevBasketRef.current = basket;
  }, [basket]);

  const showSavingsPopup = () => {
    if (!basket || basket.syncStatus !== 'SYNCED') {
      return;
    }

    const message = getMixAndSaveTierMessage(basket);

    if (message) {
      setShowSavingsBubble(true);
      setSavingsMessage(message);

      if (savingsBubbleTimer.current) {
        clearTimeout(savingsBubbleTimer.current);
      }
      savingsBubbleTimer.current = setTimeout(() => {
        setShowSavingsBubble(false);
      }, BUBBLE_POPUP_TIMEOUT);
    }
  };

  // Handle click outside for dropdown
  useEffect(() => {
    const handleClickOutside = e => {
      if (
        showDropDown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setShowDropDown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropDown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (savingsBubbleTimer.current) {
        clearTimeout(savingsBubbleTimer.current);
      }
    };
  }, []);

  const toggleDropDown = () => setShowDropDown(!showDropDown);

  const switchAccount = () => {
    toggleDropDown();
    toggleSwitchAccountModal(true);
  };

  const openInJJsModal = () => {
    setShowDropDown(false);
    openInJJsModalProp();
  };

  const logout = () => {
    toggleDropDown();
    logoutProp();
  };

  const gotoRegister = e => {
    e && e.preventDefault();
    navigateToRegister();
  };

  const isLoggedIn =
    !!auth || (!!staffLoginEmail && isStaffAccount) || userCookie;
  const renderMessage = getTopBarWelcomeText(
    profile,
    isStaffAccount,
    staffLoginEmail,
    isLoggedIn,
    userCookie
  );
  const renderSwitchAccount = (isMultiAccounts || isStaffAccount) && (
    <Option onClick={switchAccount}>Switch account</Option>
  );

  const renderCreateSignup = (
    <OptionRouterLink $noWrap to={'/newReferral'} onClick={toggleDropDown}>
      Refer a Business
    </OptionRouterLink>
  );

  const renderInvoiceUpload = isLoggedIn &&
    showPriceCompareLink(settings, isStaffAccount) && (
      <OptionRouterLink
        $noWrap
        to={PRICE_COMPARISON_PATH}
        onClick={toggleDropDown}
      >
        {PRICE_COMPARE_LABEL}
      </OptionRouterLink>
    );

  const renderApplyForCredit = isLoggedIn && (
    <OptionRouterLink $noWrap to={'/apply-for-credit'} onClick={toggleDropDown}>
      Apply for Credit
    </OptionRouterLink>
  );

  const renderShoppingListLink = showShoppingList && (
    <OptionRouterLink to={'/shoppingLists'} onClick={toggleDropDown}>
      My shopping lists
    </OptionRouterLink>
  );
  const renderCurrentAccount = auth && auth.c_account && `: ${auth.c_account}`;
  const isProd =
    typeof window !== 'undefined' &&
    window.location &&
    window.location.host === JJ_DOMAIN.PROD;
  const renderLoginByPin = !isProd && (
    <OptionRouterLink to={'/code'} onClick={toggleDropDown}>
      Login by Pin
    </OptionRouterLink>
  );
  const renderDropDown = showDropDown && (
    <DropDown>
      <OptionNoLink $active={true}>Account{renderCurrentAccount}</OptionNoLink>
      <OptionRouterLink to={'/orders'} onClick={toggleDropDown}>
        My orders
      </OptionRouterLink>
      {renderShoppingListLink}
      <OptionRouterLink to={'/account'} onClick={toggleDropDown}>
        My settings
      </OptionRouterLink>
      {renderInvoiceUpload}
      <OptionRouterLink to={'/reports'} onClick={toggleDropDown}>
        View reports
      </OptionRouterLink>
      {renderLoginByPin}
      {renderCreateSignup}
      {renderApplyForCredit}
      {renderSwitchAccount}
      <Option onClick={logout}>LOGOUT</Option>
    </DropDown>
  );
  const renderArrow = showDropDown ? <ArrowUp /> : <ArrowDown />;
  const renderStatusButton = isLoggedIn ? (
    <DropDownWrapper ref={dropdownRef}>
      <BlueButton onClick={toggleDropDown}>
        My account
        {renderArrow}
      </BlueButton>
      {renderDropDown}
    </DropDownWrapper>
  ) : (
    <Fragment>
      <BlueButton
        href="/login"
        onClick={toggleLoginModal}
        $disable={isMaintenanceMode}
      >
        Sign In / Register
      </BlueButton>
    </Fragment>
  );
  const renderCart = updatingBasketStatus ? (
    <LoadingWrapper>
      <Loading isLight={true} />
    </LoadingWrapper>
  ) : (
    <Quantity $isLoading={updatingBasketStatus} data-rw="top-bar--quantity">
      {quantity}
    </Quantity>
  );

  const renderSavingsBubble = showSavingsBubble && (
    <SavingsBubble onClick={() => setShowSavingsBubble(false)}>
      {savingsMessage}
    </SavingsBubble>
  );
  const renderSystemMessage = systemMessages && (
    <SystemStatus systemMessages={systemMessages} />
  );

  return (
    <>
      <Wrapper>
        <Container>
          <Left>
            <Menu onClick={toggleMenu} />
            <Description>{renderMessage}</Description>
            {renderStatusButton}
          </Left>
          <Right>
            <div data-nosnippet={true}>
              <OldSite to="/content/frequently-asked-questions">
                <div>Having problems?</div>
                Click here for help
              </OldSite>
            </div>
            <InfoText onClick={navigateToBookSlot}>
              {branchList && branchList[branch] && branchList[branch].Name}
            </InfoText>
            <CartWrapper>
              <Cart to="/basket" $hasMixSave={hasMixAndSaveOffer(basket)}>
                {renderCart}
                <CartText data-rw="topbar--my-basket-link">My basket</CartText>
                {hasMixAndSaveOffer(basket) && (
                  <MixSaveButton $icon={savingsIcon}>
                    <MixSaveQty>{getMixAndSaveQuantity(basket)}</MixSaveQty>
                  </MixSaveButton>
                )}
              </Cart>
              {renderSavingsBubble}
            </CartWrapper>
            <BlueButton
              onClick={navigateToCheckout}
              $disable={isMaintenanceMode}
            >
              Check out
            </BlueButton>
          </Right>
        </Container>
      </Wrapper>
      {renderSystemMessage}
    </>
  );
};

TopBar.propTypes = {
  toggleLoginModal: PropTypes.func.isRequired,
  toggleMenu: PropTypes.func.isRequired,
  quantity: PropTypes.number.isRequired,
  navigateToCheckout: PropTypes.func.isRequired,
  navigateToSurvey: PropTypes.func.isRequired,
  navigateToBookSlot: PropTypes.func.isRequired,
  navigateToRegister: PropTypes.func.isRequired,
  openInJJsModal: PropTypes.func.isRequired,
  profile: PropTypes.object,
  basket: PropTypes.object,
  isMultiAccounts: PropTypes.bool,
  isStaffAccount: PropTypes.bool,
  showShoppingList: PropTypes.bool,
  userCookie: PropTypes.string,
  settings: PropTypes.object,
};

export default TopBar;
